import { useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { getEffectiveMealTimestamp, getMealLocalDateKey, toLocalDateKey } from './utils/mealDate';

interface GoalTrackingChartProps {
  clientData: any[];
  calculatedGoals: {
    targetCalories: number;
    protein: { min: number; max: number; target: number };
    carbs: { min: number; max: number; target: number };
    fat: { min: number; max: number; target: number };
  } | null | undefined;
  timeRange: '7d' | '30d' | '90d';
}

interface GoalData {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  caloriesSuccess: number;
  proteinSuccess: number;
  carbsSuccess: number;
  fatSuccess: number;
  overallSuccess: number;
}

export default function GoalTrackingChart({ clientData, calculatedGoals, timeRange }: GoalTrackingChartProps) {
  const goalData = useMemo(() => {
    if (!clientData.length || !calculatedGoals) return [];

    const now = new Date();
    const daysToShow = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(now.getTime() - (daysToShow - 1) * 24 * 60 * 60 * 1000);

    // Group meals by date
    const mealsByDate = new Map<string, any[]>();
    
    clientData.forEach(meal => {
      const mealDate = getEffectiveMealTimestamp(meal);
      if (!mealDate) return;

      const dateKey = getMealLocalDateKey(meal);
      if (!dateKey) return;

      const mealDayStart = new Date(mealDate);
      mealDayStart.setHours(0, 0, 0, 0);
      if (mealDayStart >= startDate) {
        if (!mealsByDate.has(dateKey)) {
          mealsByDate.set(dateKey, []);
        }
        mealsByDate.get(dateKey)!.push(meal);
      }
    });

    // Create goal tracking data points
    const data: GoalData[] = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const dateKey = toLocalDateKey(date);
      const dayMeals = mealsByDate.get(dateKey) || [];

      const dayCalories = dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
      const dayProtein = dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
      const dayCarbs = dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
      const dayFat = dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

      // Calculate success percentages with proper over/under target handling
      const calculateSuccess = (actual: number, target: number) => {
        if (actual === 0) return 0;
        const ratio = actual / target;
        if (ratio >= 0.9 && ratio <= 1.1) return 100; // Perfect range (90-110%)
        if (ratio >= 0.7 && ratio <= 1.3) return 70;  // Good range (70-130%)
        if (ratio < 0.7) return Math.max(0, ratio * 100); // Under target
        return Math.max(0, 100 - (ratio - 1.1) * 50); // Over target penalty
      };

      const caloriesSuccess = calculateSuccess(dayCalories, calculatedGoals.targetCalories);
      const proteinSuccess = calculateSuccess(dayProtein, calculatedGoals.protein.target);
      const carbsSuccess = calculateSuccess(dayCarbs, calculatedGoals.carbs.target);
      const fatSuccess = calculateSuccess(dayFat, calculatedGoals.fat.target);

      const overallSuccess = (caloriesSuccess + proteinSuccess + carbsSuccess + fatSuccess) / 4;

      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        calories: dayCalories,
        protein: dayProtein,
        carbs: dayCarbs,
        fat: dayFat,
        caloriesSuccess,
        proteinSuccess,
        carbsSuccess,
        fatSuccess,
        overallSuccess
      });
    }

    return data;
  }, [clientData, calculatedGoals, timeRange]);

  const getSuccessColor = (success: number) => {
    if (success >= 90) return '#8aff8a'; // Green
    if (success >= 70) return '#ffd700'; // Yellow
    return '#ff6b6b'; // Red
  };

  const getSuccessMessage = (dayData: GoalData) => {
    const messages = [];
    
    const getCalorieMessage = () => {
      const ratio = dayData.calories / calculatedGoals!.targetCalories;
      if (ratio >= 0.9 && ratio <= 1.1) return "🎯 Perfect calorie target!";
      if (ratio >= 0.7 && ratio <= 1.3) {
        const diff = Math.round(calculatedGoals!.targetCalories - dayData.calories);
        return `📊 Calories: ${diff > 0 ? `Add ${diff}` : `Reduce ${Math.abs(diff)}`} calories`;
      }
      const diff = Math.round(calculatedGoals!.targetCalories - dayData.calories);
      return `⚠️ Calories: ${diff > 0 ? `Add ${diff}` : `Reduce ${Math.abs(diff)}`} calories`;
    };

    const getProteinMessage = () => {
      const ratio = dayData.protein / calculatedGoals!.protein.target;
      if (ratio >= 0.9 && ratio <= 1.1) return "🍗 Excellent protein intake!";
      if (ratio >= 0.7 && ratio <= 1.3) {
        const diff = Math.round(calculatedGoals!.protein.target - dayData.protein);
        return `🍗 Protein: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
      }
      const diff = Math.round(calculatedGoals!.protein.target - dayData.protein);
      return `⚠️ Protein: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
    };

    const getCarbsMessage = () => {
      const ratio = dayData.carbs / calculatedGoals!.carbs.target;
      if (ratio >= 0.9 && ratio <= 1.1) return "🍞 Great carb balance!";
      if (ratio >= 0.7 && ratio <= 1.3) {
        const diff = Math.round(calculatedGoals!.carbs.target - dayData.carbs);
        return `🍞 Carbs: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
      }
      const diff = Math.round(calculatedGoals!.carbs.target - dayData.carbs);
      return `⚠️ Carbs: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
    };

    const getFatMessage = () => {
      const ratio = dayData.fat / calculatedGoals!.fat.target;
      if (ratio >= 0.9 && ratio <= 1.1) return "🥑 Perfect fat intake!";
      if (ratio >= 0.7 && ratio <= 1.3) {
        const diff = Math.round(calculatedGoals!.fat.target - dayData.fat);
        return `🥑 Fat: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
      }
      const diff = Math.round(calculatedGoals!.fat.target - dayData.fat);
      return `⚠️ Fat: ${diff > 0 ? `Add ${diff}g` : `Reduce ${Math.abs(diff)}g`}`;
    };

    messages.push(getCalorieMessage());
    messages.push(getProteinMessage());

    return messages.slice(0, 2).join(' • '); // Show top 2 messages
  };

  const averageSuccess = goalData.length > 0 
    ? Math.round(goalData.reduce((sum, day) => sum + day.overallSuccess, 0) / goalData.length)
    : 0;

  if (!calculatedGoals) {
    return (
      <div className="bg-white rounded-lg p-4 shadow">
        <h4 className="text-lg font-semibold text-[#1f1f1f] mb-4 text-center">
          🎯 Goal Achievement Tracking
        </h4>
        <div className="text-center py-8 text-[#666]">
          Set diet goals using the calculator above to see goal tracking
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow">
      <h4 className="text-lg font-semibold text-[#1f1f1f] mb-4 text-center">
        🎯 Goal Achievement Tracking
      </h4>
      
      {/* Overall Success Summary */}
      <div className="mb-6 text-center">
        <div className={`text-3xl font-bold mb-2 ${
          averageSuccess >= 90 ? 'text-[#8aff8a]' : 
          averageSuccess >= 70 ? 'text-[#ffd700]' : 'text-[#ff6b6b]'
        }`}>
          {averageSuccess}%
        </div>
        <div className="text-sm text-[#666]">Average Goal Achievement</div>
      </div>

      {/* Goal Tracking Chart */}
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={goalData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            stroke="#666" 
            fontSize={12}
            tick={{ fill: '#666' }}
          />
          <YAxis 
            stroke="#666" 
            fontSize={12}
            tick={{ fill: '#666' }}
            domain={[0, 100]}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #d4d4d4',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            formatter={(value: number, name: string) => [
              `${Math.round(value)}%`, 
              name === 'overallSuccess' ? 'Overall Success' : name
            ]}
            labelFormatter={(label) => {
              const dayData = goalData.find(d => d.date === label);
              return dayData ? getSuccessMessage(dayData) : label;
            }}
          />
                     <Bar 
             dataKey="overallSuccess" 
             fill="#8aff8a"
             radius={[4, 4, 0, 0]}
           >
             {goalData.map((entry, index) => (
               <Cell key={`cell-${index}`} fill={getSuccessColor(entry.overallSuccess)} />
             ))}
           </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Success Legend */}
      <div className="flex justify-center space-x-6 mt-4">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-[#8aff8a]"></div>
          <span className="text-xs text-[#666]">Excellent (90%+)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-[#ffd700]"></div>
          <span className="text-xs text-[#666]">Good (70-89%)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-[#ff6b6b]"></div>
                     <span className="text-xs text-[#666]">Needs Work (&lt;70%)</span>
        </div>
      </div>

      {/* Recent Goal Insights */}
      {goalData.length > 0 && (
        <div className="mt-6 p-4 bg-gradient-to-r from-[#8aff8a]/10 to-[#0eeaff]/10 rounded-lg border border-[#d4d4d4]">
          <h5 className="font-semibold text-[#1f1f1f] mb-3">💡 Recent Insights</h5>
          <div className="space-y-2 text-sm">
            {goalData.slice(-3).map((day, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-[#666]">{day.date}:</span>
                <span className={`font-medium ${
                  day.overallSuccess >= 90 ? 'text-[#8aff8a]' : 
                  day.overallSuccess >= 70 ? 'text-[#ffd700]' : 'text-[#ff6b6b]'
                }`}>
                  {Math.round(day.overallSuccess)}% success
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 